import React from 'react';
import cn from 'classnames';

import Button from 'components/form/Button.component';
import ExternalLink from 'components/generic/ExternalLink.component';
import { useTranslation } from 'providers/GILT.provider';
import { useTheme } from 'providers/Theme.provider';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import fromAnyToString from 'utils/converters/fromAnyToString';

import CN from './AccountHolderDetails.module.scss';

const LINK = 'https://www.getslick.com/legal/terms';

// Where to get test card number: https://stripe.com/docs/testing#cards
const AccountHolderDetails = ({
  email,
  expiryMonth,
  expiryYear,
  handleUpdateAccountDetails,
  handleUpdateCardDetails,
  last4digits,
  name,
  salonId,
  salonName,
}) => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();

  const cardNumber = last4digits ? `XXXX XXXX XXXX ${last4digits}` : '---';
  const cardExpiry = fromAnyToBool(expiryMonth)
    ? `${fromAnyToString(expiryMonth).padStart(2, '0')}/${fromAnyToString(expiryYear).substr(-2)}`
    : '---';

  return (
    <div
      data-bem="AccountHolderDetails"
      className={cn({
        [CN.component]: true,
        [CN.dark]: isDarkMode,
      })}
    >
      <div className={CN.section}>
        <div className={CN.inner}>
          <div className={CN.subTitle}>{t('staffAndBusiness.subAndBilling.billingDetails.section1Title')}</div>
          <div className={CN.container}>
            <div className={CN.inputContainer}>
              <div className={CN.label}>{t('staffAndBusiness.subAndBilling.billingDetails.accountNameLabel')}</div>
              <div className={CN.value}>{name || '--'}</div>
            </div>
            <div className={CN.inputContainer}>
              <div className={CN.label}>{t('staffAndBusiness.subAndBilling.billingDetails.accountEmailLabel')}</div>
              <div className={CN.value}>{email || '--'}</div>
            </div>
          </div>

          <div className={CN.container}>
            <div className={CN.inputContainer}>
              <div className={CN.label}>{t('staffAndBusiness.subAndBilling.billingDetails.businessLabel')}</div>
              <div className={CN.value}>{salonName || '--'}</div>
            </div>
            <div className={CN.inputContainer}>
              <div className={CN.label}>{t('staffAndBusiness.subAndBilling.billingDetails.accountIdLabel')}</div>
              <div className={CN.value}>{salonId || '--'}</div>
            </div>
          </div>

          <div className={CN.buttonContainer}>
            <Button variant="primary" onClick={handleUpdateAccountDetails}>
              <div className={CN.buttonGap}>{t('staffAndBusiness.subAndBilling.billingDetails.editButton')}</div>
            </Button>
          </div>
        </div>
      </div>
      <div className={CN.section}>
        <div className={CN.inner}>
          <div className={CN.subTitle}>{t('staffAndBusiness.subAndBilling.billingDetails.section2Title')}</div>
          <div className={CN.text}>
            <p>{t('staffAndBusiness.subAndBilling.billingDetails.section2Description1')}</p>
            <div>
              {t('staffAndBusiness.subAndBilling.billingDetails.section2Description2')}
              <ExternalLink link={LINK} variant="inline">
                <div className={CN.link}>{LINK}</div>
              </ExternalLink>
            </div>
            <p>{t('staffAndBusiness.subAndBilling.billingDetails.section2Description3')}</p>
          </div>
          <div className={CN.container}>
            <div className={CN.inputContainer}>
              <div className={CN.label}>{t('staffAndBusiness.subAndBilling.billingDetails.cardNumberLabel')}</div>
              <div className={CN.value}>{cardNumber}</div>
            </div>
            <div className={CN.inputContainer}>
              <div className={CN.label}>{t('staffAndBusiness.subAndBilling.billingDetails.cardExpiryLabel')}</div>
              <div className={CN.value}>{cardExpiry}</div>
            </div>
          </div>
          <div className={CN.buttonContainer}>
            <Button variant="primary" onClick={handleUpdateCardDetails}>
              <div className={CN.buttonGap}>
                {fromAnyToBool(last4digits) && fromAnyToBool(expiryMonth)
                  ? t('staffAndBusiness.subAndBilling.billingDetails.editCardButton')
                  : t('staffAndBusiness.subAndBilling.billingDetails.addCardButton')}
              </div>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountHolderDetails;
